import React, { useCallback, useEffect, useMemo, useState } from "react";
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";

const IMAGE_NAME_REGEX = /^[a-zA-Z0-9_-]+$/;

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 10
};

const normalizeImageVariables = async ({ raw, storage }) => {
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const entries = await Promise.all(Object.entries(source).map(async ([name, meta]) => {
    const storagePath = String(meta?.storagePath || "");
    const url = storagePath
      ? await getDownloadURL(storageRef(storage, storagePath)).catch(() => meta?.url || "")
      : (meta?.url || "");
    return [name, { ...(meta || {}), name, storagePath, url }];
  }));
  return Object.fromEntries(entries);
};

const ImageVariablesSection = ({ isActive, podcastId, onImageVariablesChange }) => {
  const db = useMemo(() => getFirestore(), []);
  const storage = useMemo(() => getStorage(), []);
  const podcastDocRef = useMemo(() => {
    if (!podcastId) return null;
    return doc(db, "podcasts", String(podcastId));
  }, [db, podcastId]);

  const [imageName, setImageName] = useState("");
  const [imageVariables, setImageVariables] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const loadImageVariables = useCallback(async () => {
    if (!podcastDocRef) return;
    try {
      setLoading(true);
      setError("");
      const snap = await getDoc(podcastDocRef);
      const normalized = await normalizeImageVariables({
        raw: snap.exists() ? snap.data()?.image_variables : {},
        storage
      });
      setImageVariables(normalized);
      onImageVariablesChange?.(normalized);
    } catch (err) {
      console.error("Failed to load image variables:", err);
      setError("Failed to load image variables.");
    } finally {
      setLoading(false);
    }
  }, [podcastDocRef, storage, onImageVariablesChange]);

  useEffect(() => {
    if (!isActive || !podcastDocRef) return;
    loadImageVariables();
  }, [isActive, podcastDocRef, loadImageVariables]);

  const uploadImageVariable = async (file) => {
    const key = String(imageName || "").trim();
    if (!podcastDocRef || !podcastId) return;
    if (!key || !IMAGE_NAME_REGEX.test(key)) {
      setError("Use letters, numbers, hyphen, or underscore for the image name.");
      setStatus("");
      return;
    }
    if (!file) {
      setError("Choose an image file first.");
      setStatus("");
      return;
    }

    try {
      setUploading(true);
      setStatus("");
      setError("");

      const storagePath = `Podcasts/${String(podcastId)}/images/${key}`;
      await uploadBytes(storageRef(storage, storagePath), file, { contentType: file.type || "image/png" });
      const url = await getDownloadURL(storageRef(storage, storagePath)).catch(() => "");

      const snap = await getDoc(podcastDocRef);
      const existing = snap.exists() && snap.data()?.image_variables && typeof snap.data().image_variables === "object"
        ? snap.data().image_variables
        : {};
      const next = {
        ...existing,
        [key]: {
          name: key,
          storagePath,
          url,
          contentType: file.type || "image/png",
          uploadedAt: new Date().toISOString()
        }
      };

      await setDoc(podcastDocRef, { image_variables: next, updatedAt: serverTimestamp() }, { merge: true });
      const normalized = await normalizeImageVariables({ raw: next, storage });
      setImageVariables(normalized);
      onImageVariablesChange?.(normalized);
      setStatus(`Uploaded ${key}.`);
    } catch (err) {
      console.error("Failed to upload image variable:", err);
      setError("Failed to upload image variable.");
    } finally {
      setUploading(false);
    }
  };

  const removeImageVariable = async (key) => {
    if (!podcastDocRef || !key) return;
    try {
      setStatus("");
      setError("");
      const next = { ...imageVariables };
      delete next[key];
      await setDoc(podcastDocRef, { image_variables: next, updatedAt: serverTimestamp() }, { merge: true });
      setImageVariables(next);
      onImageVariablesChange?.(next);
      setStatus(`Removed ${key}.`);
    } catch (err) {
      console.error("Failed to remove image variable:", err);
      setError("Failed to remove image variable.");
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 800, fontSize: 16 }}>Image variables</div>
      <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.4 }}>
        Upload custom images that route scenes can reference by image name.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          value={imageName}
          onChange={(e) => setImageName(e.target.value)}
          placeholder="image_name"
          style={{
            minWidth: 180,
            padding: "9px 10px",
            borderRadius: 8,
            border: "1px solid #d1d5db",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
          }}
        />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #2563eb",
            background: "#dbeafe",
            color: "#1d4ed8",
            fontWeight: 700,
            cursor: uploading ? "default" : "pointer",
            opacity: uploading ? 0.65 : 1
          }}
        >
          {uploading ? "Uploading..." : "Upload image"}
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) uploadImageVariable(file);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />
        </label>
        <button
          onClick={loadImageVariables}
          disabled={loading || !podcastDocRef}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "#f3f4f6",
            color: "#374151",
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          {loading ? "Loading..." : "Reload"}
        </button>
      </div>

      {Object.keys(imageVariables).length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: 12
          }}
        >
          {Object.entries(imageVariables).map(([key, img]) => (
            <div
              key={key}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 10,
                background: "#f9fafb",
                display: "grid",
                gap: 8
              }}
            >
              {img?.url ? (
                <img
                  src={img.url}
                  alt={key}
                  style={{
                    width: "100%",
                    height: 84,
                    objectFit: "cover",
                    background: "#fff",
                    borderRadius: 8
                  }}
                />
              ) : (
                <div style={{ height: 84, display: "grid", placeItems: "center", color: "#6b7280" }}>No preview</div>
              )}
              <div
                title={key}
                style={{
                  fontSize: 12,
                  color: "#374151",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {key}
              </div>
              <button
                onClick={() => removeImageVariable(key)}
                style={{
                  border: "1px solid #fecaca",
                  background: "#fee2e2",
                  color: "#991b1b",
                  borderRadius: 8,
                  padding: "6px 8px",
                  fontWeight: 700,
                  cursor: "pointer"
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: "#6b7280" }}>No custom image variables uploaded yet.</div>
      )}

      {(status || error) && (
        <div style={{ color: error ? "#b91c1c" : "#065f46", fontWeight: 700 }}>
          {error || status}
        </div>
      )}
    </div>
  );
};

export default ImageVariablesSection;
